import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";

import RadioCardGroup from "../../../components/form-item/RadioCardGroup";
import Button from "../../../components/button/Button";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import ProgressMeter, {
  useProjectProgress,
} from "../../../components/progress-meter/ProgressMeter";
import CreditEditModal from "../../../components/credit/CreditEditModal";
import CreditViewModal from "../../../components/credit/CreditViewModal";
import Badge from "../../../components/badge/Badge";
// @ts-ignore
import UserList from "../../../components/project-banner/components/UserList";
import { useObservedHeight } from "../../../hooks/useObservedHeight";
import useElementRect from "../../../hooks/useElementRect";
import { Project } from "../../../app/services/projects";
import { getShotPlaybackSrc, useGetShotsQuery } from "../../../app/services/shots";
import {
  isClientGroupPosition,
  canEditCredit,
} from "../../../utils/memberPositionUtils";
import { useShortcutAction } from "../../../utils/keyboardShortcuts";

import ProjectPortraitCard, { NewProjectCard } from "./ProjectPortraitCard";

/**
 * プロジェクトリスト「進行中」「完了」タブのホイール表示（完了タブは年チップで
 * 絞り込んだ分を並べ、新規作成カードは置かない）。
 *
 * オファー作成ウィザードと同じ RadioCardGroup（GSAP、円 → 扇 → ホイール回転を
 * スクロール操作だけで完結）に縦型大判カード（ProjectPortraitCard）を並べる。
 * 進行中タブでは末尾に「新しいプロジェクトを始める」カードが常駐する（メール招待カードと
 * 同じセンチネルパターン）。
 *
 * 下部には viewport 固定で 2 段のパネルを重ねる（OfferWizardOverlay の
 * SecondaryPanel / WizardActionPanel と同じ様式・document.body へ Portal）:
 *  - 詳細パネル: アクティブ（中央）なプロジェクトの目的物 / 最終更新日 /
 *    バッジ内訳（ラベル + 青丸バッジ） / 概要 / プログレスメーター /
 *    メンバー（バナーと同じ 2 段: クライアント＋プロデューサー / スペシャリスト）
 *  - アクションパネル: 左端「クレジットを編集」（編集権限あり）または
 *    「クレジットを観る」（閲覧のみ）・右端「進行を確認」
 *
 * カードサイズは固定値ではなく「viewport から上部クローム + 下部パネルを除いた
 * 残り領域」へ白銀比（1:√2）でフィットさせ、`verticalCenterY` でその領域の
 * 縦中央にカードを寄せる。
 */

// ===== 定数 =====

/**
 * 【試作バリエーション】カード配置レイアウトの切り替えフラグ。
 *  - "horizontal": 完全な横スクロール（直線配置・回転なし）の試作パターン。
 *  - "arc": 従来の円弧（円 → 扇 → ホイール回転）。
 * 円弧に戻す場合はこの 1 行を "arc" に変更するだけでよい。
 */
const LAYOUT_VARIANT: "arc" | "horizontal" = "horizontal";

/** ホイール末尾の「新しいプロジェクトを始める」センチネルカードの ID。 */
const NEW_PROJECT_CARD_ID = "__new_project__";

/** GlobalHeader(64px) + TopAppBarHome(48px) ぶんの上部クローム高さ。 */
const TOP_CHROME_PX = 64 + 48;
/** カード領域の上下に確保する呼吸余白(px)。 */
const CARD_REGION_PADDING_PX = 16;
/** アクションパネルと viewport 下端の距離(px)。/projects に BottomAppBar は無い。 */
const PANEL_BOTTOM_PX = 16;
/** 詳細パネルとアクションパネルの間隔(px)。 */
const PANEL_GAP_PX = 8;
/** カード高さの下限(px)。viewport が極端に低くてもこれ以上は縮めない。 */
const MIN_CARD_HEIGHT_PX = 320;
/** カード左右に最低限確保する余白(px)。SP では幅基準で縮める。 */
const MIN_HORIZONTAL_INSET_PX = 48;
/** レイアウト再計算のジッター抑止しきい値(px)。これ未満の変化は無視する。 */
const LAYOUT_DAMPING_PX = 8;
/** 白銀比（カード縦横比 1:√2）。 */
const SILVER_RATIO = Math.SQRT2;
/**
 * 下部パネルの最大幅(px)。ページ側の `.u-wrapper-full`（100% - 32px）のままだと
 * ワイド画面で文字が読みにくいため、`.u-wrapper`（min(100% - 32px, 1140px)）に
 * 揃えて制限する。アンカー幅（= 100% - 32px）との min で同じ挙動になる。
 */
const PANEL_MAX_WIDTH_PX = 1140;
/** 概要テキストの最大幅(px)。`.u-wrapper-reading-paragraph`（max 640px）と同じ
 *  読みやすさ基準。 */
const OVERVIEW_MAX_WIDTH_PX = 640;
/** 概要テキストの最大文字数。フロート回り込みでは line-clamp が使えないため
 *  文字数で丸める。 */
const OVERVIEW_MAX_CHARS = 120;

/**
 * ホイールのスクロールが止まってから下部パネル（詳細 + アクション）を
 * フェード表示するまでの待ち(ms)。スクロール中は内容が目まぐるしく
 * 入れ替わって忙しないため、移動中は隠してカードが停止・確定したあとに
 * 「ふわっと」出す。
 */
const PANEL_REVEAL_DELAY_MS = 100;

/** 背景動画として扱う拡張子（MediaPreview の判定と同じ）。 */
const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "m3u8"];

const isVideoUrl = (url: string | null | undefined): url is string => {
  if (!url) return false;
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  return !!ext && VIDEO_EXTENSIONS.includes(ext);
};

// ===== 型 =====

/** RadioCardGroup に渡すアイテム。`project` 無しは新規作成センチネル。 */
interface WheelItem {
  id: string;
  project?: Project;
}

export interface ProjectWheelProps {
  projects: Project[];
  /**
   * タブが表示中か。親は Activity で本体をマウントしたまま隠すが、下部パネルは
   * document.body へ Portal しており Activity の display:none が効かないため、
   * ここが false の間は Portal ごと描画しない。
   */
  active: boolean;
  /** 末尾に「新しいプロジェクトを始める」カードを置くか（完了 / 検索タブでは置かない） */
  showNewProjectCard?: boolean;
  /** セグメント行の下に追加で置いた要素（完了タブの年チップ等）の高さ(px) */
  topInsetPx?: number;
  /** ホイールのアクセシブルネーム */
  ariaLabel?: string;
}

// ===== Styled =====

/**
 * ホイールの "スクローラ"。`data-offer-overlay-scroll`（JSX 側で付与）を
 * RadioCardGroup が自動検出して scroll listener を貼る（OfferWizardOverlay の
 * OverlayBody と同じ仕組み）。
 *
 * window スクロールではなく要素スクロールにするのは、RadioCardGroup の
 * ホイール転送（GESTURE_SCROLL_GAIN）と慣性停止後の「最寄りカードへのスナップ」が
 * 要素スクローラのときだけ有効になるため。ページ復元によるスクロール位置の
 * 持ち越しで中途半端な回転状態から始まる問題もこれで消える。
 * スクロールバーは演出の一部として非表示にする。
 */
const WheelScroller = styled.div<{ $topChromePx: number }>`
  height: calc(100vh - ${({ $topChromePx }) => $topChromePx}px);
  height: calc(100dvh - ${({ $topChromePx }) => $topChromePx}px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * スクロール連動フェードの共通スタイル（詳細パネル / アクションパネル）。
 * スクロール中は `$visible=false` で非表示（すっと消える）、停止して
 * PANEL_REVEAL_DELAY_MS 後に少し下からふわっとフェードインする。
 * opacity/transform のみの切替なので useObservedHeight の高さ計測には影響しない。
 */
const scrollFadeCss = css<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? 0 : 6)}px);
  transition: ${({ $visible }) =>
    $visible
      ? "opacity 0.45s ease, transform 0.45s ease"
      : "opacity 0.15s ease, transform 0.15s ease"};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

/**
 * アクションパネル。OfferWizardOverlay の WizardActionPanel と同じ
 * グラスモーフィズム（半透明 + backdrop-filter + 全周 border-radius）。
 * Stage(z=2) より上、モーダル類より下の z=3。
 */
const ActionPanel = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
  $visible: boolean;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 3;
  padding: 8px 12px;
  ${scrollFadeCss}

  border-radius: 6px;
  border: 1px solid #f0f0f0;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(13px);
  -webkit-backdrop-filter: blur(13px);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

/**
 * 詳細パネルの配置ラッパー（アクションパネルの真上に固定）。
 * 外枠は持たず、中身の DetailBlock が背景・余白を持つ（SecondaryPanel と同じ設計）。
 */
const DetailPanelPositioner = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
  $visible: boolean;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 3;
  ${scrollFadeCss}
`;

/**
 * 詳細パネル本体。背後を通るホイールカードがぼんやり透ける半透明 +
 * backdrop-filter（RolePreviewBlock と同じトーン）。
 */
const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 6px;
`;

/**
 * テキスト（目的物 / 最終更新日 / 概要）とアバタースタックのフロー領域。
 * アバタースタックを float: right にすることで、最終更新日の直下に余白なく
 * 概要が続き、テキストはスタックの周囲へ自然に回り込む。
 */
const DetailFlow = styled.div`
  min-width: 0;
`;

/**
 * メンバー（UserList）のフロート枠。Projects.tsx の UserListWrapper と同じく
 * アバターの hover 浮き上がりぶんを padding + 負マージンで確保しつつ、
 * 左マージンだけ正の値にしてテキストとの間隔をとる。
 */
const MembersFloat = styled.div`
  float: right;
  width: 38.2%;
  position: relative;
  overflow: hidden;
  padding: 8px 12px 14px 0;
  margin: -8px -12px -14px 12px;
`;

/** 目的物は 1 行で省略し、パネル高さのブレを抑える。
 *  （overflow: hidden が BFC を作るため float とは重ならない） */
const FinalWorkTitle = styled.h3`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/** 概要。line-clamp は float 回り込みと両立しないため素の段落にして、
 *  丸めは文字数（OVERVIEW_MAX_CHARS）で行う。
 *  幅は `.u-wrapper-reading-paragraph` と同じ 640px を上限に読みやすく保つ。 */
const OverviewText = styled.p`
  max-width: ${OVERVIEW_MAX_WIDTH_PX}px;
  text-align: justify;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * 詳細パネル用の ProgressMeter。行内で残り幅いっぱいに伸ばし、
 * バー本体とフィルの端をピル状に丸める。ProgressMeter は内部クラスを
 * 公開していないため構造セレクタで当てる（root > container > fills）。
 */
const DetailProgressMeter = styled(ProgressMeter)`
  flex: 1;
  min-width: 0;

  & > div:first-child {
    border-radius: 999px;

    > div {
      border-radius: 999px;
    }
  }
`;

// ===== 自分のバッジ（やること / 未読 / 未DL） =====

/**
 * プロジェクト一覧 API が返す「自分視点」のバッジ合計。
 *  - todo_count: やること（クライアント: レビュー待ち / プロデューサー: タスク）
 *  - unread_message_count: トークルームの未読
 *  - undownload_file_count: トークルームの未ダウンロード
 * ホイールの並び順（多い順）とカード右上の垂れ帯表示の両方に使う。
 */
const badgeTotalOf = (project: Project): number =>
  (project.todo_count ?? 0) +
  (project.unread_message_count ?? 0) +
  (project.undownload_file_count ?? 0);

// ===== 詳細パネルの中身 =====

/**
 * 進捗ラベル。ProjectBanner の formatLabel と同じ
 * 「%（検収済みショット数 / 総ショット数（保留除外））」表記。
 */
const formatProgressLabel = (
  pct: number,
  approved: number,
  denominator: number,
) => {
  if (denominator <= 0) return "0.0%（0/0）";
  return `${pct.toFixed(1)}%（${approved}/${denominator}）`;
};

const ProjectDetailContent: React.FC<{ project: Project }> = ({ project }) => {
  const progress = useProjectProgress(project);

  const approvedCount = project.approved_shots_count ?? 0;
  const shotDenominator = Math.max(
    (project.total_shots_count ?? 0) - (project.pending_shots_count ?? 0),
    0,
  );
  const progressLabel = formatProgressLabel(
    progress.approvalPercent,
    approvedCount,
    shotDenominator,
  );

  const overviewText = useMemo(() => {
    const raw = (project.overview ?? "").replace(/\s+/g, " ").trim();
    if (!raw) return "";
    return raw.length > OVERVIEW_MAX_CHARS
      ? `${raw.slice(0, OVERVIEW_MAX_CHARS)}…`
      : raw;
  }, [project.overview]);

  // 自分のバッジ内訳。「ラベル + 青丸バッジ（Badge）」の組で 1 行に並べる
  // （0 件の項目は出さない）。数字はカード右上の垂れ帯と同じブルーに揃える。
  const todoCount = project.todo_count ?? 0;
  const unreadCount = project.unread_message_count ?? 0;
  const undownloadCount = project.undownload_file_count ?? 0;
  const isClientRole = isClientGroupPosition(project.current_user_role ?? "");
  const badgeItems: { label: string; count: number }[] = [
    { label: isClientRole ? "レビュー" : "タスク", count: todoCount },
    { label: "未読", count: unreadCount },
    { label: "未ダウンロード", count: undownloadCount },
  ].filter((item) => item.count > 0);

  return (
    <DetailBlock>
      <DetailFlow>
        <MembersFloat>
          <UserList
            projectId={project.project_id}
            memberSummaries={project.member_summaries}
            currentUserRole={project.current_user_role}
            disableDetails
            combineMainGroups
          />
        </MembersFloat>
        <FinalWorkTitle className="heading-spacing-18 u-line-height-150">
          {project.final_work}
        </FinalWorkTitle>
        <div className="bodytext-11 u-text-light-gray">
          <DateDisplay
            date={project.updated_at}
            type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
          />
        </div>
        {badgeItems.length > 0 && (
          <div className="u-row u-gap12">
            {badgeItems.map(({ label, count }) => (
              <span key={label} className="u-row u-gap4">
                <span className="bodytext-11">{label}</span>
                <Badge count={count} />
              </span>
            ))}
          </div>
        )}
        {overviewText && (
          <OverviewText className="bodytext-13 u-line-height-150">
            {overviewText}
          </OverviewText>
        )}
      </DetailFlow>

      <ProgressRow>
        <DetailProgressMeter
          project={project}
          variant="compact"
          showDescription={false}
        />
        <span
          className="bodytext-11 u-text-black"
          style={{ whiteSpace: "nowrap" }}
        >
          {progressLabel}
        </span>
      </ProgressRow>
    </DetailBlock>
  );
};

const NewProjectDetailContent: React.FC = () => (
  <DetailBlock>
    <h3 className="heading-spacing-18 u-line-height-150">
      新しいプロジェクトを始める
    </h3>
    <p className="bodytext-13 u-text-light-gray u-line-height-150">
      コードネームと目的物を決めて、新しいプロジェクトを開始します。エンドクライアントやメンバーは作成後にいつでも設定できます。
    </p>
  </DetailBlock>
);

// ===== 本体 =====

const ProjectWheel: React.FC<ProjectWheelProps> = ({
  projects,
  active,
  showNewProjectCard = true,
  topInsetPx = 0,
  ariaLabel = "進行中のプロジェクトを選択",
}) => {
  const navigate = useNavigate();
  const topChromePx = TOP_CHROME_PX + topInsetPx;

  // 自分のバッジ（やること + 未読 + 未DL）が多い順に並べる。
  // 同数のときは API の並び（最終更新日降順）を保つ（Array.sort は stable）。
  const items = useMemo<WheelItem[]>(
    () => [
      ...[...projects]
        .sort((a, b) => badgeTotalOf(b) - badgeTotalOf(a))
        .map((p) => ({ id: p.project_id, project: p })),
      ...(showNewProjectCard ? [{ id: NEW_PROJECT_CARD_ID }] : []),
    ],
    [projects, showNewProjectCard],
  );

  // ── 選択状態（ホイール中央のカード = 選択中） ──
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const effectiveSelectedId =
    selectedId !== undefined && items.some((i) => i.id === selectedId)
      ? selectedId
      : items[0]?.id;
  const selectedItem = items.find((i) => i.id === effectiveSelectedId);
  const selectedProject = selectedItem?.project;
  const isNewProjectSelected = effectiveSelectedId === NEW_PROJECT_CARD_ID;

  const [creditViewOpen, setCreditViewOpen] = useState(false);
  const [creditEditOpen, setCreditEditOpen] = useState(false);

  // クレジット編集はプロジェクト完了を待たずに解禁する。
  // 権限（PRODUCER / ADMIN / CLIENT）は完了タブのカードと同じ判定。
  const canEditSelectedCredit =
    !!selectedProject && canEditCredit(selectedProject.current_user_role);

  // 選択更新で親が再レンダーされても RadioCardGroup のジェスチャ購読を維持する。
  const handleWheelChange = useCallback((id: string | number) => {
    setSelectedId(String(id));
  }, []);
  const handleWheelConfirm = useCallback(
    (_id: string | number, item: WheelItem) => {
      navigate(
        item.project ? `/${item.project.project_id}` : "/project-creation",
      );
    },
    [navigate],
  );

  // ⌥2 再押下（既にプロジェクトリスト表示中）: ホイールの選択を次のプロジェクトへ
  // 進める（→ キーと同じ 1 step 移動）。表示中のタブ（active）のホイールのみ反応する。
  // eslint-disable-next-line no-unused-vars
  const advanceStepRef = useRef<((direction: 1 | -1) => void) | null>(null);
  useShortcutAction("projects-next", () => {
    if (!active) return;
    advanceStepRef.current?.(1);
  });

  // ── スタックプレビュー（Shift ホールド）中は下部パネルを隠す ──
  const [stacked, setStacked] = useState(false);
  const handleStackChange = useCallback((stackActive: boolean) => {
    setStacked(stackActive);
  }, []);

  // ── 下部パネル（詳細 + アクション）の表示状態（スクロール移動中は隠す） ──
  // WheelScroller の scroll イベントで即座に隠し、イベントが途絶えて
  // PANEL_REVEAL_DELAY_MS 経ったら表示に戻す（デバウンス）。キー移動・クリック
  // 移動・停止時スナップの smooth scroll も scroll イベントを発火するので、
  // 「カードが完全に停止した直後にふわっと出る」挙動に自然になる。
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [panelsVisible, setPanelsVisible] = useState(true);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: number | null = null;
    const handleScroll = () => {
      // 同値 setState は React が bail out するので、スクロール中の連続呼び出しは
      // 再レンダーを起こさない。
      setPanelsVisible(false);
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        setPanelsVisible(true);
      }, PANEL_REVEAL_DELAY_MS);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  // ── 背景動画（バナー画像なしプロジェクト向け・Netflix 風） ──
  // 選択中プロジェクトにバナー画像が無い場合のみ、進行中ショットを
  // 更新日降順で引き、最初に見つかった動画メディアを背景 cover 再生に使う。
  const needsVideoLookup =
    active && !!selectedProject && !selectedProject.banner_image_optimized;
  const { data: activeShotsData } = useGetShotsQuery(
    {
      project_id: selectedProject?.project_id ?? "",
      filter: "active",
      sort_by: "updated_at",
      order: "desc",
      limit: 30,
    },
    { skip: !needsVideoLookup },
  );
  const backgroundVideo = useMemo(() => {
    if (!needsVideoLookup || !selectedProject) return undefined;
    for (const shot of activeShotsData?.data?.shots ?? []) {
      // 背景動画は自動再生されるため、変換済み（HLS）を優先して転送量を抑える
      const playbackSrc = getShotPlaybackSrc(shot);
      if (isVideoUrl(playbackSrc)) {
        return {
          projectId: selectedProject.project_id,
          url: playbackSrc,
          poster: shot.thumbnail ?? undefined,
        };
      }
    }
    return undefined;
  }, [needsVideoLookup, selectedProject, activeShotsData]);

  // ── viewport 追従 ──
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── 下部パネルの実高計測 ──
  const { ref: detailRef, height: detailHeightRaw } =
    useObservedHeight<HTMLDivElement>();
  const { ref: actionRef, height: actionHeightRaw } =
    useObservedHeight<HTMLDivElement>();

  /**
   * 詳細パネルはプロジェクトごとに高さが揺れる（概要の有無・メンバー行数）。
   * 揺れのたびにカードサイズが伸縮すると目障りなので、reserve は
   * 「これまでに観測した最大値」で単調増加させ、カードは最も背の高いパネルに
   * 合わせて一度だけ縮む。タブ非表示（Portal アンマウント）で計測が 0 に
   * 落ちてもカードサイズが跳ねないのも同じ理由。アクションパネル側も同様。
   */
  const [detailReserve, setDetailReserve] = useState(0);
  useEffect(() => {
    setDetailReserve((prev) => Math.max(prev, detailHeightRaw));
  }, [detailHeightRaw]);
  const [actionReserve, setActionReserve] = useState(0);
  useEffect(() => {
    setActionReserve((prev) => Math.max(prev, actionHeightRaw));
  }, [actionHeightRaw]);

  // ── カードサイズ算出（詳細情報・ボタンを除いた残り領域にフィット） ──
  const rawLayout = useMemo(() => {
    const topOffset = topChromePx + CARD_REGION_PADDING_PX;
    const reservedBottom =
      PANEL_BOTTOM_PX +
      actionReserve +
      PANEL_GAP_PX +
      detailReserve +
      CARD_REGION_PADDING_PX;
    const freeHeight = viewport.height - topOffset - reservedBottom;

    let cardHeight = Math.max(MIN_CARD_HEIGHT_PX, freeHeight);
    let cardWidth = cardHeight / SILVER_RATIO;
    const maxCardWidth = Math.max(
      240,
      viewport.width - MIN_HORIZONTAL_INSET_PX * 2,
    );
    if (cardWidth > maxCardWidth) {
      cardWidth = maxCardWidth;
      cardHeight = cardWidth * SILVER_RATIO;
    }

    const centerY =
      freeHeight > 0 ? topOffset + freeHeight / 2 : viewport.height / 2;

    return {
      cardWidth: Math.round(cardWidth),
      cardHeight: Math.round(cardHeight),
      centerY: Math.round(centerY),
    };
  }, [viewport, actionReserve, detailReserve, topChromePx]);

  // ジッター抑止: しきい値未満の変化では RadioCardGroup へ渡す値を更新しない。
  const [layout, setLayout] = useState(rawLayout);
  useEffect(() => {
    setLayout((prev) => {
      const stable =
        Math.abs(prev.cardWidth - rawLayout.cardWidth) < LAYOUT_DAMPING_PX &&
        Math.abs(prev.cardHeight - rawLayout.cardHeight) < LAYOUT_DAMPING_PX &&
        Math.abs(prev.centerY - rawLayout.centerY) < LAYOUT_DAMPING_PX;
      return stable ? prev : rawLayout;
    });
  }, [rawLayout]);

  const cardSize = useMemo(
    () => ({ width: layout.cardWidth, height: layout.cardHeight }),
    [layout.cardWidth, layout.cardHeight],
  );

  // ── 下部パネルの水平位置（`.u-wrapper` 相当の幅に制限して中央寄せ） ──
  const anchorRef = useRef<HTMLDivElement>(null);
  const anchorRect = useElementRect(anchorRef);
  const anchorWidth = anchorRect.width || viewport.width;
  const panelWidth = Math.min(anchorWidth, PANEL_MAX_WIDTH_PX);
  const panelLeft = anchorRect.left + (anchorWidth - panelWidth) / 2;

  const actionPanelBottom = PANEL_BOTTOM_PX;
  // 実高（Raw）基準。まだ計測前（0）のフレームでは詳細パネルが下に重なるが、
  // 次フレームの ResizeObserver で即座に補正される。
  const detailPanelBottom = PANEL_BOTTOM_PX + actionHeightRaw + PANEL_GAP_PX;

  return (
    <div ref={anchorRef}>
      <WheelScroller
        ref={scrollerRef}
        data-offer-overlay-scroll=""
        $topChromePx={topChromePx}
      >
        <RadioCardGroup<WheelItem>
          items={items}
          value={effectiveSelectedId}
          onChange={handleWheelChange}
          // Enter キーで中央カードを決定（やることホイールと操作感を揃える）。
          // プロジェクトカードはプロジェクトへ、新規作成カードは作成画面へ。
          onConfirm={handleWheelConfirm}
          // ⌥2 再押下でホイールを 1 step 進めるためのハンドル
          advanceStepRef={advanceStepRef}
          // ページ遷移直後（Stage フォーカスなし）でも ← / → のカード送りと
          // Enter の決定が効くようにする。表示中のタブ（active）のみ有効。
          globalKeyboardNav={active}
          cardSize={cardSize}
          scaleFan={1}
          breakOut
          // カード列は viewport の左右端までフルに見せる（クリップ余白なし）。
          breakOutInsetPx={0}
          verticalCenterY={layout.centerY}
          layout={LAYOUT_VARIANT}
          // ジェスチャ挙動を他のホイール（ホーム / オファー）と統一する。
          // 内部スクロールを持つカードが無いため操作は素通しになる。
          cardScrollHandoff
          // スクロール速度に応じてホイール全体をズームアウトし、
          // プロジェクトの全体数を掴みやすくする（停止で等倍に戻る）。
          // 左右キー / カードクリックのステップ移動ではズームしない。
          // minScale 0 = ループ境界のジャンプが見えない自動クランプの下限まで
          // 最大ズームアウト。
          velocityZoom={{ minScale: 0 }}
          // 選択中（中央）のカードだけ少し大きく表示する。
          // （1.1 だと低い viewport でカードの上下が見切れるため 1.05）
          activeCardScale={1.05}
          // Shift ホールドで全カードを横スタック（右が上）に集約し、
          // プロジェクトの全体量を見渡す。件数は「新規作成」センチネルを
          // 除いた実プロジェクト数を出す。表示中のタブ（active）のみ有効。
          stackPreview={active ? { count: projects.length } : undefined}
          onStackChange={handleStackChange}
          ariaLabel={ariaLabel}
          renderCard={(item, state) => {
            if (!item.project) {
              return <NewProjectCard selected={state.checked} />;
            }
            const video =
              backgroundVideo?.projectId === item.project.project_id
                ? backgroundVideo
                : undefined;
            return (
              <ProjectPortraitCard
                project={item.project}
                selected={state.checked}
                videoUrl={video?.url}
                videoPoster={video?.poster}
                badgeCount={badgeTotalOf(item.project)}
              />
            );
          }}
        />
      </WheelScroller>

      {active &&
        createPortal(
          <>
            <DetailPanelPositioner
              ref={detailRef}
              $bottom={detailPanelBottom}
              $left={panelLeft}
              $width={panelWidth}
              $visible={panelsVisible && !stacked}
            >
              {selectedProject ? (
                <ProjectDetailContent project={selectedProject} />
              ) : (
                <NewProjectDetailContent />
              )}
            </DetailPanelPositioner>

            <ActionPanel
              ref={actionRef}
              $bottom={actionPanelBottom}
              $left={panelLeft}
              $width={panelWidth}
              $visible={panelsVisible && !stacked}
            >
              {isNewProjectSelected ? (
                <>
                  <span aria-hidden="true" />
                  <Button
                    size="small"
                    theme="primary"
                    variant="filled"
                    leftIcon="add"
                    onClick={() => navigate("/project-creation")}
                  >
                    新しいプロジェクトを始める
                  </Button>
                </>
              ) : (
                <>
                  {/* 左のクレジット操作は任意アクション = secondary filled
                      （outlined は非推奨のため使わない）。 */}
                  {canEditSelectedCredit ? (
                    <Button
                      size="small"
                      theme="secondary"
                      variant="filled"
                      leftIcon="view_list"
                      onClick={() => setCreditEditOpen(true)}
                    >
                      クレジットを編集
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      theme="secondary"
                      variant="filled"
                      leftIcon="movie"
                      disabled={(selectedProject?.credit_count ?? 0) === 0}
                      onClick={() => setCreditViewOpen(true)}
                    >
                      クレジットを観る
                    </Button>
                  )}
                  <Button
                    size="small"
                    theme="primary"
                    variant="filled"
                    rightIcon="chevron_right"
                    onClick={() =>
                      selectedProject &&
                      navigate(`/${selectedProject.project_id}`)
                    }
                  >
                    進行を確認
                  </Button>
                </>
              )}
            </ActionPanel>
          </>,
          document.body,
        )}

      {active && creditViewOpen && selectedProject && (
        <CreditViewModal
          projectId={selectedProject.project_id}
          projectTitle={selectedProject.code_name}
          onClose={() => setCreditViewOpen(false)}
        />
      )}

      {active && creditEditOpen && selectedProject && (
        <CreditEditModal
          projectId={selectedProject.project_id}
          projectTitle={selectedProject.code_name}
          canEdit={canEditSelectedCredit}
          onClose={() => setCreditEditOpen(false)}
        />
      )}
    </div>
  );
};

export default ProjectWheel;
